/**
 * Stable React keys for records the domain model gives no id to.
 *
 * A charge point has an id; the connectors hanging off it do not, and two
 * identical CCS2 guns on one point are genuinely indistinguishable in the data.
 * Keying on the array index would be the usual answer and the usual bug, so the
 * key is content plus an occurrence counter: identical siblings get `…#0` and
 * `…#1`, and the pair survives a reorder that a bare index would not.
 */

export interface Keyed<T> {
  readonly key: string
  readonly item: T
  /** 1-based position, for "connector 2 of 3" style copy. */
  readonly position: number
}

export const withKeys = <T>(items: readonly T[], identity: (item: T) => string): Keyed<T>[] => {
  const seen = new Map<string, number>()

  return items.map((item, index) => {
    const raw = identity(item)
    const occurrence = seen.get(raw) ?? 0
    seen.set(raw, occurrence + 1)
    return { key: `${raw}#${occurrence}`, item, position: index + 1 }
  })
}
